<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import Canvas from "./components/canvas/Canvas.svelte";
    import ChatPanel from "./components/ui/ChatPanel.svelte";
    import TopBar from "./components/ui/TopBar.svelte";
    import Toolbar from "./components/ui/Toolbar.svelte";
    import MiniChat from "./components/ui/MiniChat.svelte";
    import Cursor from "./components/canvas/Cursor.svelte";
    import CommandPalette from "./components/commands/CommandPalette.svelte";
    import ContextMenu from "./components/commands/ContextMenu.svelte";
    import Toast from "./components/ui/Toast.svelte";
    import ShareButton from "./components/ui/ShareButton.svelte";
    import PresenceAvatars from "./components/ui/PresenceAvatars.svelte";
    import { isInitializing, channel as channelStore, theme, isChatOpen, canvasEditor, showCommandPalette } from './stores';
    import * as presence from './lib/presence';
    import logo from './assets/logo-text-large.png';
    import { initializeSpace } from './lib/space';
    import { handlePromptSubmit } from './lib/prompt';
    import { getSpaceIdFromUrl, updateUrlWithSpaceId, onSpaceIdChange } from './lib/url-state';
    import { initCommands, type ContextMenuState } from './commands';

    let canvasWrapper: Canvas = $state() as Canvas;
    let cleanupHashListener: (() => void) | null = null;
    let currentSpaceId: string | null = null;
    let loadingSpaceId: string | null = null;
    let chatPanelEl: HTMLDivElement = $state() as HTMLDivElement;

    // Splash overlay state: 'visible' -> 'fading' -> 'hidden'
    let splashState: 'visible' | 'fading' | 'hidden' = $state('visible');

    // When initialization completes, trigger fade-out
    $effect(() => {
        if (!$isInitializing && splashState === 'visible') {
            splashState = 'fading';
        }
    });

    // Context menu state
    let contextMenuState: ContextMenuState = $state({
        visible: false,
        position: { x: 0, y: 0 },
    });

    function closeContextMenu() {
        contextMenuState = { ...contextMenuState, visible: false };
    }

    function handleContextMenu(event: MouseEvent) {
        event.preventDefault();
        // On macOS, ctrl+click fires contextmenu — suppress it so ctrl+click
        // can be used for "Select Descendants" without opening the menu.
        if (event.ctrlKey && !event.metaKey) return;
        contextMenuState = {
            visible: true,
            position: { x: event.clientX, y: event.clientY },
        };
    }

    // Export canvas methods for parent components
    export function getCanvas() {
        return canvasWrapper?.getCanvas();
    }

    export function fitToNodes(nodeIds?: string[], padding?: number) {
        canvasWrapper?.fitToNodes(nodeIds, padding);
    }

    export function panTo(x: number, y: number) {
        canvasWrapper?.panTo(x, y);
    }

    export function startEditingNode(nodeId: string) {
        canvasWrapper?.startEditingNode(nodeId);
    }

    async function loadSpace(spaceId: string | null) {
        // Skip if we're already on this space (but allow null to null for initial load)
        if (spaceId === currentSpaceId && currentSpaceId !== null) {
            return;
        }

        // Skip if we're already loading this space (prevents hash-change re-trigger)
        if (spaceId && spaceId === loadingSpaceId) {
            return;
        }

        loadingSpaceId = spaceId;

        // Close current channel if open
        const currentChannel = $channelStore;
        if (currentChannel) {
            currentChannel.close();
            channelStore.set(null);
        }

        // Reset initializing state
        isInitializing.set(true);

        try {
            // Initialize the new space
            const result = await initializeSpace(spaceId || undefined);
            
            if (result) {
                currentSpaceId = result.space.id;
                // Also track the resolved ID for subsequent hash-change guards
                loadingSpaceId = result.space.id;
                
                // Update URL if:
                // 1. Space was newly created, OR
                // 2. No spaceId was in URL (opened default space)
                if (result.isNew || !spaceId) {
                    updateUrlWithSpaceId(result.space.id);
                }
            }
        } catch (error) {
            console.error("Error in loadSpace:", error);
            isInitializing.set(false);
        } finally {
            loadingSpaceId = null;
        }
    }

    onMount(async () => {
        // Load space from URL
        const spaceId = getSpaceIdFromUrl();
        await loadSpace(spaceId);

        // Set up listener for browser back/forward navigation.
        // Only triggers loadSpace for genuine back/forward, not our own programmatic hash updates.
        cleanupHashListener = onSpaceIdChange((newSpaceId) => {
            // Skip if the hash matches the space we're currently loading
            if (newSpaceId && newSpaceId === loadingSpaceId) {
                return;
            }
            // Skip if the channel store already has this space loaded
            // (e.g. switchSpace() already opened it and updated the URL)
            const ch = $channelStore;
            if (newSpaceId && ch && ch.id === newSpaceId) {
                currentSpaceId = newSpaceId;
                return;
            }
            loadSpace(newSpaceId);
        });
    });

    // Initialize commands when canvas editor is ready.
    // Re-initializes when editor changes (e.g., space navigation destroys/recreates Canvas).
    $effect(() => {
        const editor = $canvasEditor;
        if (!editor) return;

        const cleanup = initCommands(editor);
        return cleanup;
    });

    // Apply theme to document root
    $effect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', $theme);
        }
    });

    onDestroy(() => {
        // Clean up hash change listener
        if (cleanupHashListener) {
            cleanupHashListener();
        }

        // Stop presence broadcasting
        presence.stop();

        // Close current channel
        const currentChannel = $channelStore;
        if (currentChannel) {
            currentChannel.close();
        }
    });

    // Chat panel resize — drag left edge
    function startChatResize(event: PointerEvent) {
        if (!chatPanelEl) return;
        event.preventDefault();

        const startX = event.clientX;
        const startWidth = chatPanelEl.getBoundingClientRect().width;
        const minWidth = 320;
        const maxWidth = Math.round(window.innerHeight * 0.8);

        const handleMove = (moveEvent: PointerEvent) => {
            // Dragging LEFT edge means: moving left increases width
            const delta = startX - moveEvent.clientX;
            const nextWidth = Math.min(
                maxWidth,
                Math.max(minWidth, startWidth + delta)
            );
            chatPanelEl.style.width = `${nextWidth}px`;
        };

        const handleUp = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
    }
</script>

<!-- Full-bleed canvas -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="canvas-fullbleed" oncontextmenu={handleContextMenu}>
    <Canvas bind:this={canvasWrapper} />
</div>

<!-- Floating UI chrome -->
<TopBar />
<Toolbar />

<!-- Top-right: presence + share cluster -->
<div class="top-right-bar">
	<div class="top-right-group">
		<PresenceAvatars />
		<div class="top-right-divider"></div>
		<ShareButton />
	</div>
</div>

{#if $isChatOpen}
    <div class="chat-float" bind:this={chatPanelEl}>
        <div
            class="chat-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat panel"
            onpointerdown={startChatResize}
        ></div>
        <ChatPanel onSubmit={handlePromptSubmit} />
    </div>
{:else}
    <MiniChat onSubmit={handlePromptSubmit} />
{/if}

<Cursor />
<CommandPalette />
<ContextMenu menuState={contextMenuState} onClose={closeContextMenu} />
<Toast />

{#if splashState !== 'hidden'}
    <div
        class="splash-overlay"
        class:fading={splashState === 'fading'}
        ontransitionend={() => splashState = 'hidden'}
    >
        <div class="splash-content">
            <img src={logo} alt="rool" class="splash-logo" />
            <span class="splash-text">canvas</span>
            {#if $channelStore}
                <span class="splash-loading">Loading space</span>
            {/if}
        </div>
    </div>
{/if}

<style>
    /* Top-right bar: avatars + share button */
    .top-right-bar {
        position: fixed;
        top: 16px;
        right: 16px;
        z-index: 9999;
        pointer-events: auto;
    }

    .top-right-group {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 48px;
        padding: 6px 8px 6px 10px;
        background: var(--panelSolid);
        border: 1px solid var(--border);
        border-radius: 14px;
        box-shadow: var(--shadowLight);
        backdrop-filter: blur(10px);
    }

    .top-right-divider {
        width: 1px;
        align-self: stretch;
        background: var(--border);
    }

    /* Full-bleed canvas — no border-radius, no margin */
    .canvas-fullbleed {
        position: fixed;
        inset: 0;
        background: var(--bg0);
        z-index: 0;
    }

    /* Right-side floating chat panel */
    .chat-float {
        position: fixed;
        bottom: 16px;
        right: 16px;
        width: 540px;
        min-width: 320px;
        max-width: 80vh;
        /* Fill from top bar to bottom, capped at 80vh.
           A definite height is required so the chat-panel's
           flex column and overflow-y: auto on .chat-messages work. */
        top: 76px;
        max-height: calc(100vh - 76px - 16px);

        display: flex;
        z-index: 9998;
        pointer-events: auto;
    }

    .chat-resizer {
        position: absolute;
        top: 0;
        left: -5px;
        width: 10px;
        height: 100%;
        cursor: ew-resize;
        z-index: 1;
    }

    /* Splash overlay */
    .splash-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--panelSolid);
        opacity: 1;
        transition: opacity 0.4s ease-out;
    }

    .splash-overlay.fading {
        opacity: 0;
    }

    .splash-content {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
    }

    .splash-logo {
        width: 120px;
        height: auto;
    }

    .splash-text {
        font-size: 1.1rem;
        font-weight: 500;
        letter-spacing: 0.15em;
        color: var(--muted);
    }

    .splash-loading {
        position: absolute;
        top: 100%;
        margin-top: 20px;
        font-size: 0.85rem;
        font-weight: 400;
        color: var(--faint);
        animation: splashPulse 1.5s ease-in-out infinite;
    }

    @keyframes splashPulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
    }
</style>
